import React, { useState, useEffect, useRef } from "react";
import { Wrapper } from "./style";
import { MakeApiCall } from "../../../apis";
import {
    message,
    Table,
    Button,
    ConfigProvider,
    Pagination,
    Menu,
    Upload,
    Dropdown,
    Input,
    InputNumber,
    Collapse,
    Spin
} from "antd";
import { Link } from "react-router-dom";
import { fakeProImage } from "../../../config/fakeData";
import { CheckOutlined, CloseOutlined, DownloadOutlined, EditOutlined, PlusCircleOutlined, CloudUploadOutlined } from "@ant-design/icons";
import { Helmet } from "react-helmet";
import AddCogs from "./lib/addCogs";
import SyncToast from '../../../components/toast/SyncToast';
import { useProductTemplateExport } from "../../../hooks/UseProductTemplateExport";
import SetupStepsBanner from "../../../components/onboarding/Setupstepsbanner";
import FeatureLockedOverlay from "../../../components/modal/Featurelockedoverlay";

const { Panel } = Collapse;
const AMAZON_BASE_URL = "https://www.amazon.in/dp/";

const Products = (props) => {
    const { userData } = props;

    const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
    const isSpApiConnected = userData?.user?.user_data?.is_amz_sp_api_connected === 1;
    const isDataSyncing = isSpApiConnected && userData?.user?.user_data?.is_data_sync_email_sent === null;
    const trialExpired = !!(userData?.user?.user_data?.trial_expires_at && Date.now() > parseInt(userData.user.user_data.trial_expires_at, 10) * 1000);

    const [totalPage, setTotalPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [list, setList] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [sortParam, setSortParam] = useState(""); // New state for server-side sorting
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [editingCell, setEditingCell] = useState({ key: null, field: null });
    const [tempValue, setTempValue] = useState("");
    const [editingError, setEditingError] = useState({ key: null, field: null, message: "" });
    const [openPanels, setOpenPanels] = useState({});
    const [syncToastDismissed, setSyncToastDismissed] = useState(false);

    const { downloadTemplate, templateLoading } = useProductTemplateExport(userData);

    const searchDebounceRef = useRef(null);
    const prevSearchRef = useRef("");

    const [uploadedFile, setUploadedFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 992);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isDataSyncing) setSyncToastDismissed(false);
    }, [isDataSyncing]);

    const handleUpload = (file) => {
        if (!isSpApiConnected) return false;
        const rawFile = file.originFileObj || file;
        setUploadedFile(rawFile);
        message.success(`${rawFile.name} ready.`);
        return false;
    };

    const handleApplyClick = async () => {
        if (!isSpApiConnected || !uploadedFile) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', uploadedFile);
            const response = await MakeApiCall("import-product-data", "post", formData, true, {}, userData?.token);
            if (response?.status) {
                message.success("File imported successfully!");
                setUploadedFile(null);
                getProductList();
            } else {
                message.error(response?.message || "Import failed.");
            }
        } catch (error) {
            message.error("Upload failed.");
        } finally {
            setUploading(false);
        }
    };

    const getProductList = async (data) => {
        const trialExpiredLocal = !!(userData?.user?.user_data?.trial_expires_at && Date.now() > parseInt(userData.user.user_data.trial_expires_at, 10) * 1000);
        if (!isSpApiConnected || trialExpiredLocal) { setLoading(false); setList([]); return; }
        setLoading(true);

        // Build URL dynamically
        let url = `get-products?search_text=${data?.searchText ?? searchText}&page=${data?.page || page || 1}&per-page=${data?.pageSize || pageSize}`;

        // Append sort only if it exists
        const currentSort = data?.sortParam !== undefined ? data.sortParam : sortParam;
        if (currentSort) {
            url += `&sort=${currentSort}`;
        }

        const response = await MakeApiCall(url, "get", null, true, {}, userData?.token);

        if (response?.status) {
            setTotalPage(response?.data?.pagination?.totalCount || 0);
            setList(response?.data?.records || []);
        } else {
            message.warning(response?.message);
            setList([]);
        }
        setLoading(false);
    };

    const updateField = async (record, field, newValue) => {
        if (!isSpApiConnected) return;
        const finalValue = field === "tags" ? newValue : parseFloat(newValue);
        const response = await MakeApiCall(`update-product/${record.id}`, "post", { [field]: finalValue }, true, null, userData?.token);
        if (response?.status) {
            message.success("Updated successfully!");
            getProductList();
        } else {
            message.error(response?.message || "Update failed");
        }
    };

    useEffect(() => {
        const trialExpiredLocal = !!(userData?.user?.user_data?.trial_expires_at && Date.now() > parseInt(userData.user.user_data.trial_expires_at, 10) * 1000);
        if (!isSpApiConnected || trialExpiredLocal) { setLoading(false); setList([]); return; }
        const isSearchChange = prevSearchRef.current !== searchText;
        prevSearchRef.current = searchText;
        if (isSearchChange) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = setTimeout(() => {
                getProductList({ page: 1, pageSize, searchText, sortParam });
            }, 400);
            return () => clearTimeout(searchDebounceRef.current);
        }
        getProductList({ page, pageSize, searchText, sortParam });
    }, [page, pageSize, isSpApiConnected, searchText, sortParam, userData?.user?.user_data?.trial_expires_at]); // Added trial expiry

    const handleTableChange = (pagination, filters, sorter) => {
        let newSortParam = "";
        // Handle single sorter (AntD provides object or array of objects)
        const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;

        if (activeSorter.order) {
            const field = activeSorter.field;
            newSortParam = activeSorter.order === "ascend" ? field : `-${field}`;
        }

        setSortParam(newSortParam);
        setPage(1); // Reset page on sort change as per requirement
    };

    const startEditing = (e, record, field, value) => {
        if (e) e.stopPropagation();
        if (!isSpApiConnected) return;
        // Clear previous error when starting a new edit
        setEditingError({ key: null, field: null, message: "" });
        setEditingCell({ key: record.id, field });
        setTempValue(value ?? "");
    };

    const finishEditing = async (e, record, field) => {
        if (e) e.stopPropagation();
        const max100Fields = new Set(["unsellable_returns_percentage", "product_tax_code", "vat_rate"]);

        // Validate percentage fields: must be numeric, between 0 and 100 inclusive
        if (max100Fields.has(field)) {
            const numeric = parseFloat(tempValue);
            if (Number.isNaN(numeric) || numeric < 0 || numeric > 100) {
                const msg = Number.isNaN(numeric) ? "Enter a valid number" : (numeric < 0 ? "Value cannot be negative" : "Value cannot be greater than 100");
                setEditingError({ key: record.id, field, message: msg });
                message.error(msg);
                // keep editing active
                return;
            }
        }

        // Clear any error and proceed to update
        setEditingError({ key: null, field: null, message: "" });
        setEditingCell({ key: null, field: null });
        await updateField(record, field, tempValue);
    };

    const editableCell = (record, field, value) => {
        const isEditing = editingCell.key === record.id && editingCell.field === field;
        const isNumberField = field !== "tags";
        const max100Fields = new Set(["unsellable_returns_percentage", "product_tax_code", "vat_rate"]);

        if (!isEditing) {
            return (
                <div
                    className="editable-cell-container"
                    onClick={(e) => startEditing(e, record, field, value)}
                    style={{ cursor: isSpApiConnected ? "pointer" : "default", display: "inline-flex", alignItems: "center", gap: "8px", padding: "4px 8px", borderRadius: "4px" }}
                >
                    <EditOutlined style={{ color: "#f45b68", fontSize: "12px" }} />
                    <span className="text-gray-800">{value ?? "N/A"}</span>
                </div>
            );
        }

        // If this is a numeric percentage field that must be <= 100, use InputNumber with max enforced.
        if (isNumberField && max100Fields.has(field)) {
            const hasError = editingError.key === record.id && editingError.field === field && !!editingError.message;
            return (
                <div style={{ display: "flex", gap: "6px", alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
                    <InputNumber
                        autoFocus
                        size="small"
                        min={0}
                        max={100}
                        step={0.01}
                        style={{ width: 120 }}
                        value={tempValue === "" ? undefined : tempValue}
                        onChange={(val) => {
                            setTempValue(val);
                            // Inline validation while typing
                            if (val === null || val === "") {
                                setEditingError({ key: null, field: null, message: "" });
                            } else if (val > 100) {
                                setEditingError({ key: record.id, field, message: "Value cannot be greater than 100" });
                            } else if (val < 0) {
                                setEditingError({ key: record.id, field, message: "Value cannot be negative" });
                            } else {
                                setEditingError({ key: null, field: null, message: "" });
                            }
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { if (!hasError) finishEditing(e, record, field); else { e.stopPropagation(); e.preventDefault(); message.error(editingError.message); } } }}
                    />
                    <CheckOutlined
                        onClick={(e) => {
                            e.stopPropagation();
                            if (hasError) {
                                message.error(editingError.message || "Fix the input before saving");
                                return;
                            }
                            finishEditing(e, record, field);
                        }}
                        style={{ color: hasError ? "#bfbfbf" : "green", cursor: hasError ? "not-allowed" : "pointer" }}
                    />
                    <CloseOutlined onClick={(e) => { e.stopPropagation(); setEditingCell({ key: null, field: null }); setEditingError({ key: null, field: null, message: "" }); }} style={{ color: "red", cursor: "pointer" }} />
                    {hasError && (
                        <div style={{ color: 'red', fontSize: 12, marginLeft: 8 }}>{editingError.message}</div>
                    )}
                </div>
            );
        }

        // Default input for other fields (text or number)
        return (
            <div style={{ display: "flex", gap: "6px", alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
                <Input
                    autoFocus
                    size="small"
                    type={isNumberField ? "number" : "text"}
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    style={{ width: "120px" }}
                    onPressEnter={(e) => finishEditing(e, record, field)}
                />
                <CheckOutlined onClick={(e) => finishEditing(e, record, field)} style={{ color: "green", cursor: "pointer" }} />
                <CloseOutlined onClick={(e) => { e.stopPropagation(); setEditingCell({ key: null, field: null }); setEditingError({ key: null, field: null, message: "" }); }} style={{ color: "red", cursor: "pointer" }} />
            </div>
        );
    };

    const columns = [
        { title: "#", key: "index", fixed: 'left', width: 50, render: (text, record, index) => (page - 1) * pageSize + index + 1 },
        {
            title: "Product Details", key: "productDetails", fixed: 'left', width: 300,
            render: (_, record) => (
                <div className="d-flex align-items-center bdr-left-tbl bg-gray-200 rounded-4 p-3 py-2">
                    <div className="symbol symbol-65px overflow-hidden me-3">
                        <img src={record?.item_image || fakeProImage} alt="" />
                    </div>
                    <div className="d-flex flex-column">
                        <a
                            className="text-gray-800 fs-6 text-hover-danger mb-1 fw-bolder text-one min-w-200px"
                            href={record?.asin ? `${AMAZON_BASE_URL}${record.asin}` : "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {record?.item_name || ""}
                        </a>
                        <div className="fs-7 mt-1"><span><b>SKU:</b> {record?.sku}</span> / <span><b>ASIN:</b> {record?.asin}</span></div>
                        <div className="fs-7 mt-1"><span><b>{record?.currency} {record?.item_price}</b></span> / <span> {record?.item_type}</span></div>
                    </div>
                </div>
            ),
        },
        { title: "Tags", dataIndex: "tags", key: "tags", render: (value, record) => editableCell(record, "tags", value) },
        {
            title: "COGS",
            dataIndex: "item_cogs",
            key: "item_cogs",
            sorter: true,
            // Add this to highlight the icon
            sortOrder: sortParam === 'item_cogs' ? 'ascend' : sortParam === '-item_cogs' ? 'descend' : null,
            render: (cogs, record) => (
                <div className="d-flex align-items-center px-2">
                    {cogs || "N/A"}
                    <Link
                        to=""
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); isSpApiConnected && handleEditCogs(record); }}
                        style={{ marginLeft: "10px", color: isSpApiConnected ? "#333" : "#ccc" }}
                    >
                        <i className="ki-outline ki-plus-circle" />
                    </Link>
                </div>
            ),
        },
        {
            title: "Replenishment Time (Days)",
            dataIndex: "replenishment_time",
            key: "replenishment_time",
            render: (value, record) => editableCell(record, "replenishment_time", value),
            sorter: true,
            sortOrder: sortParam === 'replenishment_time' ? 'ascend' : sortParam === '-replenishment_time' ? 'descend' : null,
        },
        {
            title: "FBM shipping cost",
            dataIndex: "fbm_shipping_cost_per_package",
            key: "fbm_shipping_cost_per_package",
            render: (value, record) => editableCell(record, "fbm_shipping_cost_per_package", value),
            sorter: true,
            sortOrder: sortParam === 'fbm_shipping_cost_per_package' ? 'ascend' : sortParam === '-fbm_shipping_cost_per_package' ? 'descend' : null,
        },
        {
            title: "Val. of unsellable return (%)",
            dataIndex: "unsellable_returns_percentage",
            key: "unsellable_returns_percentage",
            render: (value, record) => editableCell(record, "unsellable_returns_percentage", value),
            sorter: true,
            sortOrder: sortParam === 'unsellable_returns_percentage' ? 'ascend' : sortParam === '-unsellable_returns_percentage' ? 'descend' : null,
        },
        {
            title: "Tax Paid on COGS (%)",
            dataIndex: "product_tax_code",
            key: "product_tax_code",
            render: (value, record) => editableCell(record, "product_tax_code", value),
            sorter: true,
            sortOrder: sortParam === 'product_tax_code' ? 'ascend' : sortParam === '-product_tax_code' ? 'descend' : null,
        },
        {
            title: "Tax Collected on Sales (%)",
            dataIndex: "vat_rate",
            key: "vat_rate",
            render: (value, record) => editableCell(record, "vat_rate", value),
            sorter: true,
            sortOrder: sortParam === 'vat_rate' ? 'ascend' : sortParam === '-vat_rate' ? 'descend' : null,
        }
    ];

    const ProductMobileCard = ({ record }) => {
        const panelKey = `tax-${record.id}`;
        const isEditingInPanel = editingCell.key === record.id && ["unsellable_returns_percentage", "product_tax_code", "vat_rate"].includes(editingCell.field);
        return (
            <div className="card border mb-4 shadow-none rounded-3 overflow-hidden">
                <div className="card-header bg-light py-3 px-4">
                    <div className="d-flex align-items-center w-100">
                        <img src={record?.item_image || fakeProImage} alt="" style={{ width: 50, height: 50, borderRadius: 6, marginRight: 12, objectFit: 'contain', background: '#fff' }} />
                        <div className="d-flex flex-column flex-grow-1 overflow-hidden">
                            <a className="text-gray-800 fw-bolder text-hover-danger text-truncate mb-0 fs-7" href={record?.asin ? `${AMAZON_BASE_URL}${record.asin}` : "#"} target="_blank" rel="noopener noreferrer">{record?.item_name}</a>
                            <div className="fs-9 text-muted"><b>SKU:</b> {record?.sku} / <b>ASIN:</b> {record?.asin}</div>
                        </div>
                    </div>
                </div>
                <div className="card-body p-4">
                    <div className="row g-4">
                        <div className="col-6"><label className="fs-9 text-muted text-uppercase fw-bold mb-1">Tags</label><div>{editableCell(record, "tags", record.tags)}</div></div>
                        <div className="col-6"><label className="fs-9 text-muted text-uppercase fw-bold mb-1">COGS</label><div className="d-flex align-items-center fs-8 ps-2 mt-1">{record.item_cogs || "N/A"}<PlusCircleOutlined className="ms-2 text-danger" onClick={(e) => { e.stopPropagation(); handleEditCogs(record); }} /></div></div>
                        <div className="col-6"><label className="fs-9 text-muted text-uppercase fw-bold mb-1">Replenishment Time (Days)</label><div>{editableCell(record, "replenishment_time", record.replenishment_time)}</div></div>
                        <div className="col-6"><label className="fs-9 text-muted text-uppercase fw-bold mb-1">FBM shipping cost</label><div>{editableCell(record, "fbm_shipping_cost_per_package", record.fbm_shipping_cost_per_package)}</div></div>
                    </div>
                </div>
                <Collapse
                    ghost
                    activeKey={(openPanels[panelKey] || isEditingInPanel) ? ['1'] : []}
                    onChange={(keys) => { if (!isEditingInPanel) setOpenPanels(prev => ({ ...prev, [panelKey]: keys.includes('1') })); }}
                    className="border-top bg-light-soft"
                >
                    <Panel header={<span className="fs-9 fw-bold text-muted text-uppercase">Tax & Return Details</span>} key="1">
                        <div className="row g-4 px-2 pb-3">
                            <div className="col-6"><label className="fs-9 text-muted text-uppercase fw-bold mb-1">Val. of unsellable return (%)</label><div>{editableCell(record, "unsellable_returns_percentage", record.unsellable_returns_percentage)}</div></div>
                            <div className="col-6"><label className="fs-9 text-muted text-uppercase fw-bold mb-1">Tax Paid on COGS (%)</label><div>{editableCell(record, "product_tax_code", record.product_tax_code)}</div></div>
                            <div className="col-6"><label className="fs-9 text-muted text-uppercase fw-bold mb-1">Tax Collected on Sales (%)</label><div>{editableCell(record, "vat_rate", record.vat_rate)}</div></div>
                        </div>
                    </Panel>
                </Collapse>
            </div>
        );
    };

    const handleEditCogs = (record) => { setSelectedProduct(record); setIsModalVisible(true); };
    const handleCloseModal = () => { setIsModalVisible(false); setSelectedProduct(null); };
    const handleSearch = (e) => { setSearchText(e.target.value); setPage(1); };

    const menu = (
        <Menu className="p-2 shadow-sm" style={{ borderRadius: '8px', minWidth: '250px' }}>
            <div className="px-3 py-2 border-bottom">
                <span className="fw-bold text-gray-800 fs-7">Bulk Import Products</span>
            </div>
            <Menu.Item key="1" className="py-3" disabled={!isSpApiConnected || templateLoading} onClick={downloadTemplate}>
                <div className="d-flex align-items-center justify-content-between w-100">
                    <div className="d-flex flex-column">
                        <span className="fs-7 fw-bold">1. Download Template</span>
                        <span className="text-muted" style={{ fontSize: '10px' }}>Fetch all product records</span>
                    </div>
                    {templateLoading ? <Spin size="small" /> : <DownloadOutlined className="text-danger fs-5" />}
                </div>
            </Menu.Item>
            <Menu.Divider />
            <div className="px-3 py-2">
                <span className="fs-7 fw-bold d-block mb-2">2. Upload Updated File</span>
                <Upload
                    beforeUpload={handleUpload}
                    fileList={uploadedFile ? [uploadedFile] : []}
                    onRemove={() => setUploadedFile(null)}
                    maxCount={1}
                    disabled={!isSpApiConnected}
                >
                    <Button
                        icon={<CloudUploadOutlined />}
                        disabled={!isSpApiConnected}
                        className="w-100 fs-7 h-40px border-dashed border-danger text-danger bg-light-danger bg-opacity-10"
                    >
                        {uploadedFile ? 'Change File' : 'Choose File'}
                    </Button>
                </Upload>
            </div>
            <Menu.Divider />
            <div className="px-3 py-2 d-flex gap-2">
                <Button size="small" className="flex-grow-1" onClick={() => setUploadedFile(null)}>
                    Reset
                </Button>
                <Button
                    size="small"
                    type="primary"
                    danger
                    className="flex-grow-1"
                    onClick={handleApplyClick}
                    disabled={uploading || !uploadedFile || !isSpApiConnected}
                >
                    {uploading ? 'Processing...' : 'Apply Import'}
                </Button>
            </div>
        </Menu>
    );

    return (
        <Wrapper>
            <Helmet><title>Product</title></Helmet>
            <SetupStepsBanner userData={userData} />
            <style>{`.editable-cell-container:hover { background-color: #f8f9fa; border: 1px dashed #f45b68; }`}</style>

            <SyncToast
                visible={isDataSyncing && !syncToastDismissed}
                onDismiss={() => setSyncToastDismissed(true)}
            />

            <div style={{ position: 'relative' }}>
                <FeatureLockedOverlay locked={trialExpired || !isSpApiConnected} title="Features Locked" message={trialExpired ? "Your trial has expired. Please renew your subscription to continue." : "Connect your Amazon account to unlock analytics."}/>
                <div className="mb-10 mx-n9 px-10 py-5 bg-white filter">
                    <div className="position-relative">
                        <i className="ki-outline ki-magnifier text-gray-800 fs-2 position-absolute top-50 translate-middle-y ms-4" />
                        <input
                            type="text"
                            value={searchText}
                            onChange={handleSearch}
                            disabled={!isSpApiConnected}
                            className="form-control form-control-solid text-dark w-300px border border-gray-300 fs-7 ps-12 all-search"
                            placeholder="Search sku, asin, title..."
                        />
                    </div>
                </div>

                <div className="row gy-5 g-xl-5">
                    <div className="col-xxl-12">
                        <div className="card card-xxl-stretch mb-5 mb-xl-8">
                            <div className="card-header border-bottom-dashed">
                                <h3 className="card-title fw-bolder">Products List</h3>
                                <div className="card-toolbar">
                                    <Dropdown overlay={menu} trigger={["click"]} disabled={!isSpApiConnected} placement="bottomRight">
                                        <button className="btn btn-sm btn-light-danger fs-7 fw-bold border border-danger border-opacity-25" disabled={!isSpApiConnected}>
                                            <i className="bi bi-box-arrow-in-down fs-5 me-1"/> Import Products
                                        </button>
                                    </Dropdown>
                                </div>
                            </div>
                            <div className="card-body py-2">
                                {loading ? (
                                    <div className="text-center py-10"><Spin size="large" /></div>
                                ) : (
                                    isMobile
                                        ? <div className="p-2">{list.map(item => <ProductMobileCard key={item.id} record={item} />)}</div>
                                        : (
                                            <div className="table-responsive">
                                                <Table
                                                    scroll={{ x: "max-content" }}
                                                    columns={columns}
                                                    dataSource={list}
                                                    rowKey="id"
                                                    pagination={false}
                                                    onChange={handleTableChange} // Capture sorting events
                                                    components={{
                                                        table: (p) => <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7" {...p} />,
                                                        header: { row: (p) => <tr {...p} className="fw-bolder text-dark bg-cus" /> }
                                                    }}
                                                />
                                            </div>
                                        )
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row gy-5 g-xl-5">
                    <div className="col-xxl-12">
                        <div className="card card-xxl-stretch mb-5 mb-xl-8">
                            <div className="card-body py-2">
                                <ConfigProvider theme={{ token: { colorPrimary: "#f45b68" } }}>
                                    <Pagination
                                        className="mt-3"
                                        current={page}
                                        pageSize={pageSize}
                                        total={totalPage}
                                        disabled={!isSpApiConnected}
                                        showSizeChanger
                                        responsive={true}
                                        simple={isMobile}
                                        onChange={(p, s) => { setPage(p); setPageSize(s); }}
                                        showTotal={(t, r) => isMobile ? `Total: ${t}` : `${r[0]}-${r[1]} of ${t} items`}
                                    />
                                </ConfigProvider>
                            </div>
                        </div>
                    </div>
                </div>

                <AddCogs
                    visible={isModalVisible}
                    onClose={handleCloseModal}
                    product={selectedProduct}
                    userData={userData}
                    getList={() => getProductList()}
                    isUpdate={!!(selectedProduct?.item_cogs)}
                />
            </div>
        </Wrapper>
    );
};

export default Products;